import { buildFilmstrip } from '@/features/preview/filmstrip';
import { ffmpegClient } from '@/services/ffmpegClient';
import { useProjectStore } from '@/store/projectStore';
import { toast } from '@/store/toastStore';
import { loadVideoMetadata, validateVideoFile } from '@/utils/validation';

function revokeAll(urls: Array<string | null | undefined>) {
  for (const url of urls) {
    if (url) URL.revokeObjectURL(url);
  }
}

function releaseCurrentSource() {
  const { sourceUrl, filmstrip } = useProjectStore.getState();
  revokeAll([sourceUrl, ...filmstrip]);
}

/**
 * Validate → probe → publish. Only after the browser proves it can decode
 * the file does it become the project source; the filmstrip renders in the
 * background afterwards so the editor is interactive immediately.
 */
export async function openVideoFile(file: File): Promise<boolean> {
  const verdict = await validateVideoFile(file);
  if (!verdict.ok) {
    toast.error("Can't use that file", verdict.error.message);
    return false;
  }

  const sourceUrl = URL.createObjectURL(file);
  let probed: { duration: number; width: number; height: number };
  try {
    probed = await loadVideoMetadata(sourceUrl);
  } catch (error) {
    URL.revokeObjectURL(sourceUrl);
    const message = error instanceof Error ? error.message : 'Unknown decode error.';
    toast.error("Can't read that video", message);
    return false;
  }

  // Any in-flight conversion belongs to the previous source.
  ffmpegClient.cancelActive();
  releaseCurrentSource();

  useProjectStore.getState().setSource(file, sourceUrl, {
    name: file.name,
    size: file.size,
    type: file.type,
    duration: probed.duration,
    width: probed.width,
    height: probed.height,
  });

  void buildFilmstrip(sourceUrl, probed.duration).then((frames) => {
    const state = useProjectStore.getState();
    if (state.sourceUrl === sourceUrl) state.setFilmstrip(frames);
    else revokeAll(frames); // a newer source replaced us mid-capture
  });

  return true;
}

/** Full teardown: stop the engine, revoke every URL, return to the empty state. */
export function resetProject(): void {
  ffmpegClient.cancelActive();
  releaseCurrentSource();
  useProjectStore.getState().resetAll();
}
